import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Modal, Spin, message } from "antd";
import { LeftOutlined, RightOutlined, HomeOutlined } from "@ant-design/icons";
import { getDay, nextDay, prevDay, todayKey, dayIndex, type PlanDay, type ReadingPart } from "../services/plan";
import {
  getDefaultVersionId,
  getAvailableVersions,
  getVersionById,
  normalizeBook,
  loadVerseRanges,
  loadBibleMetadata,
  loadSupplemental,
  isRecoveryVersionId,
  type LoadedVerse,
  type BibleSupplemental,
  type BibleOutline,
  type BibleBook,
} from "../services/bible";
import {
  buildRecoveryUnits,
  buildBookIndexMap,
  FLAG_ORDER,
  type RecoveryUnit,
  type RecoveryFootnoteMark,
} from "../services/recovery";
import FootnoteModalStack from "./FootnoteModalStack";
import { renderVerseText, type ModalDescriptor, type FootnoteData } from "../services/annotationRefs";
import { useLocale } from "../locales/IntlProvider";
import { versionLanguageToLocale, VERSION_SELECTION_KEY } from "../services/bibleLocaleDefaults";
import { formatReadingReference, formatChapterNumber, isChineseLang } from "../services/refFormat";
import { useSpeech, type SpeechSegment } from "../hooks/useSpeech";
import VerseBlock from "./VerseBlock";
import CheckinPanel from "./CheckinPanel";
import MyCheckins from "./MyCheckins";
import AuthPanel, { AUTH_CHANGED_EVENT, getLocalUser } from "./AuthPanel";
import BottomToolbar, { type ThemeName } from "./BottomToolbar";
import { applySeo } from "../services/seo";
import { seoForLocale } from "../services/seoMeta";

const HIGHLIGHT_KEY = "inoneyear-highlighted";
const FONT_SIZE_KEY = "inoneyear-font-size";
const THEME_KEY = "inoneyear-theme";
// 与 holybible 一致：主版本不染色，其余版本按顺序取色
const VERSION_COLORS = ["inherit", "#2a6496", "#4a8c3f", "#c0573a", "#8b5cf6"];
const verseKey = (v: LoadedVerse) => `${v.Book}|${v.Chapter}|${v.Verse}`;

interface RecoveryMergedVerse {
  verse: LoadedVerse;
  text: string;
  parts: LoadedVerse[];
}

interface VersionIndex {
  byKey: Map<string, LoadedVerse>;
  recovery: Map<string, RecoveryMergedVerse>;
}

interface ComparisonRow {
  versionId: string;
  colorIdx: number;
  isAnn: boolean;
  verse: LoadedVerse;
  text: string;
  parts: LoadedVerse[] | null;
}

function loadHighlights(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(HIGHLIGHT_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function loadFontSize(): number {
  try {
    const v = parseInt(localStorage.getItem(FONT_SIZE_KEY) || "", 10);
    if (v >= 12 && v <= 28) return v;
  } catch {}
  return 16;
}

function loadTheme(): ThemeName {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === "light" || v === "dark" || v === "classic") return v;
  } catch {}
  return "classic";
}

function loadSavedVersionIds(fallback: string): string[] {
  try {
    const raw = localStorage.getItem(VERSION_SELECTION_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const valid = parsed.filter((id): id is string => typeof id === "string" && !!getVersionById(id));
        const unique = Array.from(new Set(valid));
        if (unique.length > 0) return unique.slice(0, 5);
      }
    }
  } catch {}
  return [fallback];
}

// 按语言分组后的可选版本（参照 holybible 的 sortedAvailableVersions）
function sortedAvailableVersions(locale: string) {
  const base = (locale || "").toLowerCase();
  const baseLang =
    base === "zh-tw" || base === "zh-hk" || base === "zh-mo" || base === "zh-hant"
      ? "zh-hant"
      : base === "zh" || base.startsWith("zh")
        ? "zh"
        : base;
  const versions = getAvailableVersions();
  const order = new Map(versions.map((v, i) => [v.id, i]));
  return [...versions].sort((a, b) => {
    const aIdx = order.get(a.id) ?? 999;
    const bIdx = order.get(b.id) ?? 999;
    const aPri = baseLang && a.language === baseLang ? 0 : 1;
    const bPri = baseLang && b.language === baseLang ? 0 : 1;
    return aPri - bPri || aIdx - bIdx;
  });
}

const EMPTY_SUPP: BibleSupplemental = { footnotes: [], outlines: [], topics: [] };

const ReadingPage: React.FC = () => {
  const intl = useIntl();
  const { locale, setLocale } = useLocale();
  const [dateKey, setDateKey] = useState<string>(todayKey);
  const [day, setDay] = useState<PlanDay | null>(null);
  const [partsByVersion, setPartsByVersion] = useState<Record<string, { old: LoadedVerse[]; new: LoadedVerse[] }>>({});
  const [supp, setSupp] = useState<BibleSupplemental>(EMPTY_SUPP);
  const [bookIndexMap, setBookIndexMap] = useState<Map<string, string>>(new Map());
  const [versionIds, setVersionIds] = useState<string[]>(() => loadSavedVersionIds(getDefaultVersionId(locale)));
  const [loading, setLoading] = useState(true);
  const [highlights, setHighlights] = useState<Set<string>>(loadHighlights);
  const [user, setUser] = useState<string | null>(getLocalUser);
  const [fontSize, setFontSize] = useState<number>(loadFontSize);
  const [theme, setTheme] = useState<ThemeName>(loadTheme);
  const [modalStack, setModalStack] = useState<ModalDescriptor[]>([]);
  const [outlineModal, setOutlineModal] = useState<BibleOutline[] | null>(null);
  const [books, setBooks] = useState<BibleBook[]>([]);
  const [checkinCalendarOpen, setCheckinCalendarOpen] = useState(false);

  const primaryVersionId = versionIds[0] || getDefaultVersionId(locale);
  const annotationVersionId = versionIds.find((v) => v.startsWith("ChiRcV")) || primaryVersionId;
  const isPrimaryRecovery = isRecoveryVersionId(primaryVersionId);
  const isAnnotationRecovery = isRecoveryVersionId(annotationVersionId);
  const primaryLang = getVersionById(primaryVersionId)?.language || "";

  // UI 语言跟随主版本语言（参照 holybible：界面文字以排序第 1 的版本语言显示）
  useEffect(() => {
    const meta = getVersionById(primaryVersionId);
    if (meta) setLocale(versionLanguageToLocale(meta.language));
  }, [primaryVersionId, setLocale]);

  useEffect(() => {
    try {
      localStorage.setItem(VERSION_SELECTION_KEY, JSON.stringify(versionIds));
    } catch {}
  }, [versionIds]);

  const toggleVersion = (id: string) => {
    setVersionIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) return prev;
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= 5) return prev;
      // 先来后到：先勾选的版本排前面（第 1 个为主版本，界面语言随它切换）
      return [...prev, id];
    });
  };

  useEffect(() => {
    try {
      localStorage.setItem(FONT_SIZE_KEY, String(fontSize));
    } catch {}
  }, [fontSize]);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {}
  }, [theme]);

  // 把主题 CSS 变量同步到 body，让弹窗/下拉等 portal 也能跟随主题
  useEffect(() => {
    const shell = document.querySelector<HTMLElement>(".app-shell");
    if (!shell) return;
    const cs = getComputedStyle(shell);
    const names = ["--bg", "--ink", "--muted", "--accent", "--card-bg", "--border", "--menu-bg", "--menu-ink"];
    names.forEach((n) => {
      const v = cs.getPropertyValue(n).trim();
      if (v) document.body.style.setProperty(n, v);
    });
    return () => {
      names.forEach((n) => document.body.style.removeProperty(n));
    };
  }, [theme]);

  useEffect(() => {
    // 标题/描述/关键词按当前语言输出，语言范围与圣经译本一致（27 种），其余回退英文
    const seo = seoForLocale(locale);
    applySeo(locale, seo.title, seo.description, seo.keywords);
  }, [locale]);

  // 数据加载：并行加载每个选中版本的当日经文；注解版本额外加载纲目/主题/注解与书卷表
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const d = getDay(dateKey);
    setDay(d || null);
    if (!d) {
      setPartsByVersion({});
      setSupp(EMPTY_SUPP);
      setLoading(false);
      return;
    }
    Promise.all([
      Promise.all(
        versionIds.map(async (vid) => {
          const [oldV, newV] = await Promise.all([
            loadVerseRanges(vid, d.old.ranges),
            loadVerseRanges(vid, d.new.ranges),
          ]);
          return { vid, parts: { old: oldV, new: newV } as { old: LoadedVerse[]; new: LoadedVerse[] } };
        })
      ),
      loadSupplemental(annotationVersionId),
      loadBibleMetadata(annotationVersionId),
    ])
      .then(([versionParts, sup, meta]) => {
        if (cancelled) return;
        const next: Record<string, { old: LoadedVerse[]; new: LoadedVerse[] }> = {};
        for (const vp of versionParts) next[vp.vid] = vp.parts;
        setPartsByVersion(next);
        setSupp(sup as BibleSupplemental);
        setBooks(meta.books);
        setBookIndexMap(buildBookIndexMap(meta.books));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        message.error(intl.formatMessage({ id: "common.error" }));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateKey, versionIds, annotationVersionId, locale, intl]);

  useEffect(() => {
    const refresh = () => setUser(getLocalUser());
    window.addEventListener("storage", refresh);
    window.addEventListener(AUTH_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener(AUTH_CHANGED_EVENT, refresh);
    };
  }, []);

  const prev = prevDay(dateKey);
  const next = nextDay(dateKey);
  const isToday = dateKey === todayKey();
  const dayNumber = Math.max(0, dayIndex(dateKey)) + 1;

  const handleToggleHighlight = useCallback((v: LoadedVerse) => {
    const key = verseKey(v);
    setHighlights((prevSet) => {
      const nextSet = new Set(prevSet);
      if (nextSet.has(key)) nextSet.delete(key);
      else nextSet.add(key);
      try {
        localStorage.setItem(HIGHLIGHT_KEY, JSON.stringify([...nextSet]));
      } catch {}
      return nextSet;
    });
  }, []);

  const dateLabel = useMemo(() => {
    const now = new Date();
    const [m, d] = dateKey.split(".").map(Number);
    const dt = new Date(now.getFullYear(), m - 1, d);
    return intl.formatDate(dt, { year: "numeric", month: "long", day: "numeric" });
  }, [dateKey, intl]);

  const primaryParts = partsByVersion[primaryVersionId] || { old: [], new: [] };

  // Localized book names of the primary version, used to build multilingual references.
  const primaryBookNames = useMemo(() => {
    const m = new Map<string, string>();
    const parts = partsByVersion[primaryVersionId];
    if (parts) {
      for (const v of [...parts.old, ...parts.new]) {
        const key = normalizeBook(v.Book);
        if (!m.has(key)) m.set(key, v.bookName);
      }
    }
    return m;
  }, [partsByVersion, primaryVersionId]);

  const resolvePartBook = useCallback(
    (bookId: string): string => primaryBookNames.get(normalizeBook(bookId)) || bookId,
    [primaryBookNames]
  );

  // Chinese keeps the original raw reference (Chinese numerals); other languages are generated per version language.
  const partRef = useCallback(
    (part: ReadingPart | undefined): string => {
      if (!part) return "";
      if (isChineseLang(primaryLang)) return part.raw;
      return formatReadingReference(part.ranges, resolvePartBook, primaryLang);
    },
    [primaryLang, resolvePartBook]
  );

  const renderChapterHeading = (bookId: string, chapter: string, showBook: boolean) => {
    const zh = isChineseLang(primaryLang);
    const num = formatChapterNumber(parseInt(chapter, 10) || 0, primaryLang);
    const label = showBook
      ? zh
        ? `${resolvePartBook(bookId)} 第${num}章`
        : `${resolvePartBook(bookId)} ${num}`
      : zh
        ? `第${num}章`
        : `Chapter ${num}`;
    return <div className="chapter-heading">{label}</div>;
  };

  const newUnits = useMemo<RecoveryUnit[] | null>(() => {
    if (!isPrimaryRecovery) return null;
    return buildRecoveryUnits(primaryParts.new, supp.footnotes, supp.outlines, bookIndexMap);
  }, [isPrimaryRecovery, primaryParts.new, supp, bookIndexMap]);

  const oldUnits = useMemo<RecoveryUnit[] | null>(() => {
    if (!isPrimaryRecovery) return null;
    return buildRecoveryUnits(primaryParts.old, supp.footnotes, supp.outlines, bookIndexMap);
  }, [isPrimaryRecovery, primaryParts.old, supp, bookIndexMap]);

  // 各版本经文索引：byKey 按 (Book|Chapter|Verse) 匹配；恢复版额外按 1/3/2 分段合并成单行
  const versionIndex = useMemo<Record<string, VersionIndex>>(() => {
    const idx: Record<string, VersionIndex> = {};
    for (const vid of versionIds) {
      const parts = partsByVersion[vid];
      const byKey = new Map<string, LoadedVerse>();
      const recovery = new Map<string, RecoveryMergedVerse>();
      if (parts) {
        const all = [...parts.old, ...parts.new];
        for (const v of all) {
          const k = `${normalizeBook(v.Book)}|${v.Chapter}|${v.Verse}`;
          if (!byKey.has(k)) byKey.set(k, v);
        }
        if (isRecoveryVersionId(vid)) {
          const groups = new Map<string, LoadedVerse[]>();
          for (const v of all) {
            const k = `${normalizeBook(v.Book)}|${v.Chapter}|${v.Verse}`;
            const list = groups.get(k);
            if (list) list.push(v);
            else groups.set(k, [v]);
          }
          for (const [k, list] of groups) {
            const sorted = [...list].sort((a, b) => {
              const fa = FLAG_ORDER.indexOf(String(a.Flag || "0"));
              const fb = FLAG_ORDER.indexOf(String(b.Flag || "0"));
              return (fa < 0 ? 99 : fa) - (fb < 0 ? 99 : fb);
            });
            const parts = sorted.filter((p) => String(p.Text).trim() !== "a");
            recovery.set(k, { verse: sorted[0], text: parts.map((p) => p.Text).join(""), parts });
          }
        }
      }
      idx[vid] = { byKey, recovery };
    }
    return idx;
  }, [versionIds, partsByVersion]);

  // 语音播报：只读主版本经节正文，不读章节号与注解
  const speechSegments = useMemo<SpeechSegment[]>(() => {
    const segs: SpeechSegment[] = [];
    const pushPart = (label: string, units: RecoveryUnit[] | null, rawVerses: LoadedVerse[]) => {
      if (units) {
        if (units.length === 0) return;
        segs.push({ text: label, key: null });
        for (const u of units) {
          if (u.isPrologue || u.isMergedNote) continue;
          segs.push({ text: u.text.replace(/^\d+\s*/, ""), key: u.key });
        }
        return;
      }
      if (rawVerses.length === 0) return;
      segs.push({ text: label, key: null });
      for (const v of rawVerses) {
        segs.push({ text: v.Text.replace(/^\d+\s*/, ""), key: verseKey(v) });
      }
    };
    pushPart(intl.formatMessage({ id: "bible.newTestament" }), newUnits, primaryParts.new);
    pushPart(intl.formatMessage({ id: "bible.oldTestament" }), oldUnits, primaryParts.old);
    return segs;
  }, [primaryParts, newUnits, oldUnits, intl]);

  const speech = useSpeech(speechSegments, locale);

  // 语音播报时滚动到当前经节
  useEffect(() => {
    if (!speech.currentKey) return;
    const el = document.getElementById(`verse-${speech.currentKey}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [speech.currentKey]);

  const pushAnnotationFootnote = (fn: FootnoteData, bookId: string, chapter: string) => {
    setModalStack((prev) => [
      ...prev,
      {
        id: `fn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        bookId,
        chapter: parseInt(chapter, 10),
        footnotes: [fn],
      },
    ]);
  };

  const buildComparisonRow = (vid: string, book: string, chapter: string, verse: string): ComparisonRow | null => {
    const colorIdx = versionIds.indexOf(vid);
    if (colorIdx <= 0) return null;
    const idx = versionIndex[vid];
    if (!idx) return null;
    const key = `${normalizeBook(book)}|${chapter}|${verse}`;
    if (isRecoveryVersionId(vid)) {
      const m = idx.recovery.get(key);
      if (!m) return null;
      return { versionId: vid, colorIdx, isAnn: vid === annotationVersionId, verse: m.verse, text: m.text, parts: m.parts };
    }
    const v = idx.byKey.get(key);
    if (!v) return null;
    return { versionId: vid, colorIdx, isAnn: vid === annotationVersionId, verse: v, text: v.Text, parts: null };
  };

  const renderRowContent = (row: ComparisonRow, book: string, chapter: string, verse: string) => {
    const color = VERSION_COLORS[row.colorIdx];
    if (row.isAnn && isAnnotationRecovery) {
      const verseForRender =
        row.parts && row.parts.length > 0
          ? { ...row.verse, Text: row.text, Flag: "0", mergedRecoveryParts: row.parts }
          : row.verse;
      return renderVerseText(
        verseForRender,
        supp.footnotes,
        books,
        row.verse.Book,
        parseInt(chapter, 10),
        String(row.verse.Flag || "0"),
        `${row.versionId}-${chapter}-${verse}-`,
        (fn: FootnoteData) => pushAnnotationFootnote(fn, row.verse.Book, chapter)
      );
    }
    return <span style={color !== "inherit" ? { color } : undefined}>{row.text}</span>;
  };

  const renderComparisonLines = (book: string, chapter: string, verse: string) => {
    const rows = versionIds
      .map((vid) => buildComparisonRow(vid, book, chapter, verse))
      .filter((r): r is ComparisonRow => !!r);
    if (rows.length === 0) return null;
    return (
      <div className="verse-lines">
        {rows.map((row) => (
          <div key={row.versionId} className={`verse-line ${row.isAnn ? "verse-line-annotated" : ""}`}>
            {renderRowContent(row, book, chapter, verse)}
          </div>
        ))}
      </div>
    );
  };

  const renderPart = (titleId: string, part: ReadingPart | undefined, verses: LoadedVerse[]) => {
    if (verses.length === 0) return null;
    let lastBookKey = "";
    let lastChapterKey = "";
    return (
      <section className="part-block">
        <h2 className="part-title">{intl.formatMessage({ id: titleId })}</h2>
        <div className="part-raw">{partRef(part)}</div>
        {verses.map((pv) => {
          const bookKey = normalizeBook(pv.Book);
          const chapter = String(pv.Chapter);
          const verse = String(pv.Verse);
          const isChapterStart = bookKey !== lastBookKey || chapter !== lastChapterKey;
          const showBook = isChapterStart && bookKey !== lastBookKey;
          lastBookKey = bookKey;
          lastChapterKey = chapter;
          const block = (
            <VerseBlock
              verse={pv}
              versionIds={versionIds}
              highlighted={highlights.has(verseKey(pv))}
              speaking={speech.currentKey === verseKey(pv)}
              onToggleHighlight={() => handleToggleHighlight(pv)}
            />
          );
          const lines = renderComparisonLines(pv.Book, chapter, verse);
          const content = lines ? (
            <div className="verse-container multi-version-block">
              {block}
              {lines}
            </div>
          ) : (
            <div>{block}</div>
          );
          return (
            <React.Fragment key={verseKey(pv)}>
              {isChapterStart && renderChapterHeading(pv.Book, chapter, showBook)}
              {content}
            </React.Fragment>
          );
        })}
      </section>
    );
  };

  const getBookName = (bookId: string): string => {
    const book = books.find((b) => b._id === bookId || b.book_id === bookId);
    return book?.name || bookId;
  };

  const pushFootnoteFromMark = (u: RecoveryUnit, mark: RecoveryFootnoteMark) => {
    setModalStack((prev) => [
      ...prev,
      {
        id: `fn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        bookId: u.bookIndex,
        chapter: parseInt(u.chapter, 10),
        footnotes: [
          {
            _id: mark._id,
            seq: mark.seq,
            note: mark.note,
            verse: mark.verse || u.verse,
            location: mark.location,
            flag: mark.flag || u.sampleVerse.Flag,
          },
        ],
      },
    ]);
  };

  const renderRecoveryPart = (titleId: string, part: ReadingPart | undefined, units: RecoveryUnit[] | null) => {
    if (!units || units.length === 0) return null;
    let lastBookKey = "";
    let lastChapterKey = "";
    const seenBooks = new Set<string>();
    return (
      <section className="part-block">
        <h2 className="part-title">{intl.formatMessage({ id: titleId })}</h2>
        <div className="part-raw">{partRef(part)}</div>
        {units.map((u) => {
          const bookKey = normalizeBook(u.book);
          const chapter = String(u.chapter);
          const isChapterStart = bookKey !== lastBookKey || chapter !== lastChapterKey;
          const showBook = isChapterStart && bookKey !== lastBookKey;
          lastBookKey = bookKey;
          lastChapterKey = chapter;
          const isNewBook = !!u.bookIndex && !seenBooks.has(u.bookIndex);
          if (u.bookIndex) seenBooks.add(u.bookIndex);
          const topic = isNewBook
            ? supp.topics.find((t) => t.book_index === u.bookIndex)?.topic || ""
            : "";
          const outlineNodes = u.outlines.map((o) => (
            <div
              key={o._id}
              className={`outline-heading outline-level-${o.level || "1"}`}
              onClick={(e) => {
                e.stopPropagation();
                setOutlineModal(
                  supp.outlines
                    .filter((x) => x.book_index === u.bookIndex && String(x.chapter) === String(u.chapter))
                    .sort(
                      (x, y) =>
                        (parseInt(x.section, 10) || 0) - (parseInt(y.section, 10) || 0) ||
                        (parseInt(x.level, 10) || 0) - (parseInt(y.level, 10) || 0)
                    )
                );
              }}
            >
              {o.outline}
            </div>
          ));
          const block = (
            <VerseBlock
              verse={u.sampleVerse}
              versionIds={versionIds}
              text={u.text}
              footnotes={u.footnotes}
              onOpenFootnote={(mark) => pushFootnoteFromMark(u, mark)}
              isPrologue={u.isPrologue}
              mergedNote={
                u.isMergedNote
                  ? intl.formatMessage({ id: "reading.mergedNote" }, { a: u.prevVerse || "", b: u.verse })
                  : null
              }
              highlighted={highlights.has(u.key)}
              speaking={speech.currentKey === u.key}
              onToggleHighlight={() => handleToggleHighlight(u.sampleVerse)}
            />
          );
          const lines = u.isMergedNote ? null : renderComparisonLines(u.sampleVerse.Book, u.chapter, u.verse);
          return (
            <React.Fragment key={u.key}>
              {isChapterStart && renderChapterHeading(u.book, u.chapter, showBook)}
              {topic && (
                <div className="book-topic">
                  <strong>{intl.formatMessage({ id: "reading.topic" })}{intl.formatMessage({ id: "reading.topicColon" })}</strong>
                  {topic}
                </div>
              )}
              {outlineNodes}
              {lines ? (
                <div className="verse-container multi-version-block">{block}{lines}</div>
              ) : (
                block
              )}
            </React.Fragment>
          );
        })}
      </section>
    );
  };

  const hasAny = isPrimaryRecovery
    ? (newUnits?.length || 0) > 0 || (oldUnits?.length || 0) > 0
    : primaryParts.old.length > 0 || primaryParts.new.length > 0;

  return (
    <div
      className={`app-shell theme-${theme}`}
      style={{ "--reading-font-size": `${fontSize}px` } as React.CSSProperties}
    >
      <div className="reading-header">
        <div className="header-date">{dateLabel}</div>
        <div className="header-sub">{intl.formatMessage({ id: "reading.dayOfPlan" }, { n: dayNumber })}</div>
        <div className="header-login">
          <AuthPanel theme={theme} />
        </div>
      </div>

      <div className="reading-nav">
        <Button icon={<LeftOutlined />} disabled={!prev} onClick={() => prev && setDateKey(prev.date)}>
          {intl.formatMessage({ id: "reading.prevDay" })}
        </Button>
        <Button icon={<HomeOutlined />} disabled={isToday} onClick={() => setDateKey(todayKey())}>
          {intl.formatMessage({ id: "reading.today" })}
        </Button>
        <Button disabled={!next} onClick={() => next && setDateKey(next.date)}>
          {intl.formatMessage({ id: "reading.nextDay" })}
          <RightOutlined />
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : !hasAny ? (
        <div style={{ padding: 24, textAlign: "center", color: "#888" }}>
          {intl.formatMessage({ id: "reading.noPlan" })}
        </div>
      ) : isPrimaryRecovery ? (
        <>
          {renderRecoveryPart("bible.newTestament", day?.new, newUnits)}
          {renderRecoveryPart("bible.oldTestament", day?.old, oldUnits)}
        </>
      ) : (
        <>
          {renderPart("bible.newTestament", day?.new, primaryParts.new)}
          {renderPart("bible.oldTestament", day?.old, primaryParts.old)}
        </>
      )}

      <CheckinPanel dateKey={dateKey} author={user} />
      <MyCheckins
        open={checkinCalendarOpen}
        theme={theme}
        onClose={() => setCheckinCalendarOpen(false)}
        onSelectDate={(k) => {
          setDateKey(k);
          setCheckinCalendarOpen(false);
        }}
      />

      <BottomToolbar
        fontSize={fontSize}
        onFontSizeChange={setFontSize}
        theme={theme}
        onThemeChange={setTheme}
        speechSupported={speech.supported}
        speechState={speech.state}
        speechCanPlay={speech.canPlay}
        onToggleSpeech={speech.toggle}
        versionIds={versionIds}
        availableVersions={sortedAvailableVersions(locale)}
        onToggleVersion={toggleVersion}
        onOpenMyCheckins={() =>
          user
            ? setCheckinCalendarOpen(true)
            : message.info(intl.formatMessage({ id: "auth.benefits" }))
        }
      />

      <FootnoteModalStack
        versionId={annotationVersionId}
        books={books}
        footnotes={supp.footnotes}
        getBookName={getBookName}
        stack={modalStack}
        onPush={(m) => setModalStack((prev) => [...prev, m])}
        onBack={() => setModalStack((prev) => prev.slice(0, -1))}
        onClose={() => setModalStack([])}
      />

      <Modal
        open={!!outlineModal}
        onCancel={() => setOutlineModal(null)}
        footer={null}
        title={intl.formatMessage({ id: "reading.outline" })}
        width={560}
      >
        <div className="outline-modal-body">
          {outlineModal &&
            outlineModal.map((o) => (
              <div key={o._id} className={`outline-heading outline-level-${o.level || "1"}`}>
                {o.outline}
              </div>
            ))}
        </div>
      </Modal>
    </div>
  );
};

export default ReadingPage;
